import { Label, Search } from "@monsterindia/global-ui-components";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { API_ENDPOINT } from "../../../../constants/appConstant";
import { useNetwork } from "../../../../hooks/useNetwork";
import {
  ApopProps,
  CheckboxOptionType,
  DropdownOptionType,
  MasterDataType,
} from "../../../../types";
import { KeyValueType } from "../../../../types/common";
import { MultiDropDownController } from "../../../controllers";
import { ActionButtonContainer } from "../../common/ActionButtonContainer";
import { Heading } from "../../common/Heading";

type DataObj = {
  [key: string]: string | number | boolean | null | undefined | KeyValueType;
};

type AutoApplyPreferencesApopProps = Pick<
  ApopProps,
  "toast" | "closeApop" | "handleEventTracking"
> & {
  prefilledData?: Record<string, unknown>;
  hideAutoApplyPopUp?: () => void;
  onSubmitHandler?: (preferenceData: Record<string, unknown>) => void;
};

export const AutoApplyPreferencesApop = ({
  toast,
  prefilledData,
  closeApop,
  hideAutoApplyPopUp,
  onSubmitHandler,
}: AutoApplyPreferencesApopProps) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
  } = useForm();

  const [preferredJobTitleData, setPreferredJobTitleData] = useState<
    CheckboxOptionType[] | null
  >(null);
  const [preferredLocationsData, setPreferredLocationsData] = useState<
    CheckboxOptionType[] | null
  >(null);
  const [skillsData, setSkillsData] = useState<CheckboxOptionType[] | null>(
    null,
  );
  const [isDropdownOptionLoading, setIsDropdownOptionLoading] = useState(false);

  const { response: apiResponse, fetchData: submitFetchApi } = useNetwork();
  const { fetchData: fetchAPI } = useNetwork();

  const areArraysEqualById = (
    firstArray: DataObj[],
    secondArray: KeyValueType[],
  ): boolean => {
    if (firstArray.length !== secondArray.length) {
      return false;
    }

    const firstIds = new Set(firstArray.map((item) => item.id));

    return secondArray.every((item) => firstIds.has(item.id));
  };

  const prefillDataHelper = (prefill: Record<string, unknown>) => {
    const { preferredJobTitle, preferredLocations, skills } = prefill;

    if (skills) {
      setValue(
        "skills",
        (skills as DataObj[]).map((skill) => ({
          value: skill.value,
          id: skill.id,
          text: skill.text,
        })),
      );
    }

    if (preferredJobTitle) {
      setValue(
        "preferredJobTitle",
        (preferredJobTitle as DataObj[]).map((jobTitle) => ({
          value: jobTitle.value,
          id: jobTitle.id,
          text: jobTitle.text,
        })),
      );
    }

    if (preferredLocations) {
      setValue(
        "preferredLocations",
        (preferredLocations as DataObj[]).map((location) => ({
          value: location.value,
          id: location.id,
          text: location.text,
        })),
      );
    }
  };

  useEffect(() => {
    if (prefilledData) {
      prefillDataHelper(prefilledData);
    }
  }, [prefilledData]);

  const preferredJobTitleOptionsData = async (title: string) => {
    setIsDropdownOptionLoading(true);
    const res = await fetchAPI(
      `${API_ENDPOINT().seekerProfile.masterDataMiddleware}?appName=srp-ui&type=DESIGNATION&limit=10&name=${title}`,
      "GET",
    );

    const masterPreferredJobTitleData = (res.data as any)
      ?.data as MasterDataType[];

    setPreferredJobTitleData(
      masterPreferredJobTitleData?.length
        ? masterPreferredJobTitleData.map((jobTitle) => ({
            value: jobTitle.name,
            id: jobTitle.uuid,
            text: jobTitle.name,
          }))
        : null,
    );
    setIsDropdownOptionLoading(false);
  };

  const preferredLocationsOptionsData = async (location: string) => {
    setIsDropdownOptionLoading(true);
    const res = await fetchAPI(
      `${API_ENDPOINT().seekerProfile.masterDataMiddleware}?appName=srp-ui&type=LOCATION&limit=10&name=${location}`,
      "GET",
    );

    const masterPreferredLocationsData = (res.data as any)
      ?.data as MasterDataType[];

    setPreferredLocationsData(
      masterPreferredLocationsData?.length
        ? masterPreferredLocationsData.map((item) => ({
            value: item.name,
            id: item.uuid,
            text: item.name,
          }))
        : null,
    );
    setIsDropdownOptionLoading(false);
  };

  const skillsDropdownOptionsData = async (skill: string) => {
    setIsDropdownOptionLoading(true);
    const res = await fetchAPI(
      `${API_ENDPOINT().seekerProfile.masterDataMiddleware}?appName=srp-ui&type=SKILL&limit=10&name=${skill}`,
      "GET",
    );

    const masterSkillsData = (res.data as any)?.data as MasterDataType[];

    setSkillsData(
      masterSkillsData?.length
        ? masterSkillsData.map((item) => ({
            value: item.name,
            id: item.uuid,
            text: item.name,
          }))
        : null,
    );
    setIsDropdownOptionLoading(false);
  };

  const onSubmit = async (data: Record<string, unknown>) => {
    const updatedData: Record<string, unknown> = {};

    if (
      !areArraysEqualById(
        data.preferredJobTitle as DataObj[],
        (prefilledData?.preferredJobTitle as KeyValueType[]) ?? [],
      )
    ) {
      updatedData.roles = data.preferredJobTitle;
    }

    if (
      !areArraysEqualById(
        data.preferredLocations as DataObj[],
        (prefilledData?.preferredLocations as KeyValueType[]) ?? [],
      )
    ) {
      updatedData.locations = data.preferredLocations;
    }

    if (
      !areArraysEqualById(
        data.skills as DataObj[],
        (prefilledData?.skills as KeyValueType[]) ?? [],
      )
    ) {
      updatedData.skills = data.skills;
    }

    onSubmitHandler?.(updatedData);
  };

  return (
    <>
      <Heading
        headingText="AI Auto Apply Preferences"
        isMobile={window.innerWidth < 768}
        onBackBtnClickHandler={hideAutoApplyPopUp}
        onCloseBtnClickHandler={() => closeApop()}
      />

      <form
        className="l1-relative l1-mb-16 l1-mt-14 l1-flex l1-w-full l1-flex-col l1-gap-4 md:l1-mb-12 md:l1-mt-12"
        id="autoApplyPreferencesDetailsForm"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <Label
          labelText="Preferred Job Title (max 5)"
          isRequired="true"
          className="!l1-text-sm"
        >
          <MultiDropDownController
            name="preferredJobTitle"
            control={control}
            isRequired={true}
            isSearchable={true}
            maxSelect={5}
            options={preferredJobTitleData ?? []}
            isLoading={isDropdownOptionLoading}
            onSelect={(selected: DropdownOptionType) => {
              clearErrors("preferredJobTitle");
              setValue("preferredJobTitle", selected);
            }}
            onChangeHandler={preferredJobTitleOptionsData}
            placeholder={
              getValues("preferredJobTitle") &&
              getValues("preferredJobTitle").length >= 5
                ? "Max limit reached"
                : getValues("preferredJobTitle") &&
                    getValues("preferredJobTitle").length > 0
                  ? "Add another"
                  : "Enter or select your preferred job titles"
            }
            errorMessage={
              errors.preferredJobTitle ? "This field is required" : ""
            }
            isDisabled={false}
            leftIcon={<Search />}
          />
        </Label>

        <Label
          labelText="Preferred locations (max 5)"
          isRequired={true}
          className="!l1-text-sm"
        >
          <MultiDropDownController
            name="preferredLocations"
            control={control}
            isRequired={true}
            isSearchable={true}
            maxSelect={5}
            options={preferredLocationsData ?? []}
            onSelect={(selected: DropdownOptionType) => {
              clearErrors("preferredLocations");
              setValue("preferredLocations", selected);
            }}
            isLoading={isDropdownOptionLoading}
            onChangeHandler={preferredLocationsOptionsData}
            placeholder={
              getValues("preferredLocations") &&
              getValues("preferredLocations").length >= 5
                ? "Max limit reached"
                : getValues("preferredLocations") &&
                    getValues("preferredLocations").length > 0
                  ? "Add another"
                  : "Enter or select your preferred job location"
            }
            isDisabled={false}
            errorMessage={
              errors.preferredLocations ? "This field is required" : ""
            }
            leftIcon={<Search />}
          />
        </Label>

        <Label
          labelText="Skills (max 50)"
          isRequired={true}
          className="!l1-text-sm"
        >
          <MultiDropDownController
            name="skills"
            control={control}
            isRequired={true}
            isSearchable={true}
            maxSelect={50}
            options={skillsData ?? []}
            onSelect={(selected: DropdownOptionType) => {
              clearErrors("skills");
              setValue("skills", selected);
            }}
            isLoading={isDropdownOptionLoading}
            onChangeHandler={skillsDropdownOptionsData}
            placeholder={
              getValues("skills") && getValues("skills").length >= 50
                ? "Max limit reached"
                : getValues("skills") && getValues("skills").length > 0
                  ? "Add another"
                  : "Enter or select your preferred skills"
            }
            isDisabled={false}
            errorMessage={errors.skills ? "This field is required" : ""}
            leftIcon={<Search />}
            rawText={true}
          />
        </Label>

        <div className="l1-w-full l1-bg-[#FFF6E5] l1-p-2 l1-text-sm l1-text-content-primary">
          Note: You can deactivate this feature from &quot;Settings&quot;
        </div>

        <ActionButtonContainer
          primaryBtnLabel="Confirm & Apply"
          isLoading={apiResponse.isLoading}
        />
      </form>
    </>
  );
};
